import React, { useState } from 'react';

import { Typography } from 'antd';
import styled from 'styled-components';

import { useAppSelector } from 'store/hook';

import LabelsList from '../../../common/List/LabelsList';
import { CommunityIncentiveType } from '../../../types/incentives';

const { Paragraph } = Typography;

const Container = styled.span`
  display: flex;
  margin: 2px 0px 10px 0px;
  padding-right: 20px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Icon = styled.span`
  padding-top: 4px;
  padding-right: 20px;
`;

const Info = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  font-size: 12px;
`;

const Description = styled(Paragraph)`
  font-family: var(--font-regular);
  font-size: 12px;
  text-align: justify;
  line-height: normal;
  color: var(--text-secondary);
  white-space: break-spaces;
`;

const TextContainer = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-secondary);
`;

const TruncatedText = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  white-space: normal;
`;

const ExpandedText = styled.div`
  white-space: break-spaces;
`;

const ElipsisSymbol = styled.span`
  color: var(--dark-blue);
  cursor: pointer;
`;

type Props = {
  item: CommunityIncentiveType;
};

const CommunityIncentivesTableIncentives = ({ item }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const livingTypes = useAppSelector((state) => state.global.livingTypes);
  const text = item?.description;
  const isMultiParagraph = text?.includes('\n');

  const careTypes = livingTypes
    .filter((livingType) => item.living_types.includes(livingType.id))
    .map((livingType) => livingType.type);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const ParagraphsWithBreaks = ({ text }: { text: string }) => (
    <div>
      {text.split('\n').map((paragraph, index, arr) => (
        <span key={index}>
          {paragraph}
          {index < arr.length - 1 && <br />}
        </span>
      ))}
    </div>
  );

  return (
    <Container>
      <Icon>
        <img
          src={'/icons/price-tag-green.svg'}
          alt="Incentive"
          style={{
            minWidth: 24,
            minHeight: 24,
          }}
        />
      </Icon>

      <Content>
        <Info>
          <LabelsList options={careTypes ?? []} labelPlural={'care types'} />
        </Info>
        {!isMultiParagraph && (
          <Description
            ellipsis={{
              rows: 2,
              expandable: 'collapsible',
              symbol: (expanded: boolean) => <ElipsisSymbol>{expanded ? 'Show Less' : 'Show More'}</ElipsisSymbol>,
            }}
          >
            {item?.description}
          </Description>
        )}
        {isMultiParagraph && (
          <div>
            <TextContainer>
              {isExpanded && (
                <ExpandedText>
                  <ParagraphsWithBreaks text={text} />
                  <ElipsisSymbol onClick={handleToggle}> Show Less</ElipsisSymbol>
                </ExpandedText>
              )}

              {!isExpanded && (
                <>
                  <TruncatedText>
                    <ParagraphsWithBreaks text={text} />
                  </TruncatedText>
                  <ElipsisSymbol onClick={handleToggle}> Show More</ElipsisSymbol>
                </>
              )}
            </TextContainer>
          </div>
        )}
      </Content>
    </Container>
  );
};

export default CommunityIncentivesTableIncentives;
